import React, { useEffect, useRef } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Flex, Grid, Text, Button
} from 'components/ui';
import store from 'Store';
import Actions from 'Actions';
import UiUtils from 'utils/UI';
import { CARD_WIDTH, CARD_GAP } from 'constants/promotionCard';
import { space } from 'style/config';

import LanguageLocaleUtils from 'utils/LanguageLocale';
import EN from 'components/Bcc/BccRwdPersonalizedPromoList/PromoListFallback/locales/PromoListFallback_en_US.js';
import FR from 'components/Bcc/BccRwdPersonalizedPromoList/PromoListFallback/locales/PromoListFallback_fr_CA.js';

import PageRenderReport from 'components/PageRenderReport/PageRenderReport';
import { HEADER_VALUE } from 'constants/authentication';
import anaConsts from 'analytics/constants';
import content from 'constants/content';
import { sendCmsComponentEvent, matchContexts } from 'analytics/utils/cmsComponents';

const { SKELETON_TEXT } = UiUtils;
const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(EN, FR);

const {
    COMPONENT_TYPES: { PROMOTION_LIST }
} = content;
const {
    CMS_COMPONENT_EVENTS: { IMPRESSION }
} = anaConsts;

function PromoListFallback({
    isAnonymous,
    isSkeleton,
    enablePageRenderTracking = false,
    sid,
    title,
    personalization,
    extraSotFields,
    clientRequestId,
    user
}) {
    const impressionSent = useRef(false);

    // When the personalization response is default the fallback (empty-state / sign-in card) replaces
    // the promo carousel, so the carousel's viewable impression never fires. Emit the same CMS
    // impression here so the default bucketing (AB test / isControl / isDefault ...) is still captured.
    // The p13n bucketing lives in the resolved context data (matchContexts), which is carried on a
    // single item so Adobe Launch builds the beacon; extraSotFields carries the logged-out device id
    // and clientRequestId carries the ML tracking id (sotV437). Skipped while the skeleton shows and
    // for legacy callers that do not provide a sid.
    useEffect(() => {
        if (isSkeleton || impressionSent.current || typeof sid === 'undefined') {
            return;
        }

        impressionSent.current = true;

        const resolvedP13n = matchContexts(personalization);
        const mlClientRequestId = clientRequestId || resolvedP13n?.client_request_id;

        // Build tracking payload with required fields
        const itemTrackingData = {
            sid,
            p13n: resolvedP13n,
            ...(mlClientRequestId && { clientRequestId: mlClientRequestId })
        };

        // Include atg_id for signed-in users
        if (!isAnonymous && user?.userId) {
            itemTrackingData.atgId = user.userId;
        }

        // Include AB test fields from resolved p13n data
        // sotV443: activeABTestIds (abTestId)
        // sotV444: AbTestEndDate
        if (resolvedP13n?.abTestId) {
            itemTrackingData.sotV443 = resolvedP13n.abTestId;
        }

        if (resolvedP13n?.abTestEndDate) {
            itemTrackingData.sotV444 = resolvedP13n.abTestEndDate;
        }

        // Include isNBOEnabled if available from P13N response
        if (typeof personalization?.isNBOEnabled !== 'undefined') {
            itemTrackingData.isNBOEnabled = personalization.isNBOEnabled;
        }

        sendCmsComponentEvent({
            items: [itemTrackingData],
            eventName: IMPRESSION,
            component: PROMOTION_LIST,
            title,
            sid,
            p13n: personalization,
            ...(extraSotFields && { nbcTrackingData: extraSotFields })
        });
    }, [isSkeleton, sid, title, personalization, extraSotFields, clientRequestId, isAnonymous, user]);

    return (
        <Flex
            flexDirection='column'
            justifyContent='center'
            boxShadow='light'
            borderRadius={2}
            paddingY={[4, 5]}
            paddingX={[4, 6]}
            minHeight={150}
            maxWidth={CARD_WIDTH * 2 + space[CARD_GAP[1]]}
        >
            <Text
                is='h2'
                fontWeight='bold'
                marginRight='auto'
                css={isSkeleton && SKELETON_TEXT}
                children={isAnonymous ? `${getText('signInHeading')} 👋` : `${getText('noPromosHeading')} ✨`}
            />
            <Text
                is='p'
                css={
                    isSkeleton && [
                        {
                            position: 'relative',
                            top: 8
                        },
                        SKELETON_TEXT
                    ]
                }
                children={getText(isAnonymous ? 'signInText' : 'noPromosText')}
            />
            {isAnonymous && !isSkeleton && (
                <Grid
                    marginTop={4}
                    columns={2}
                    gap={4}
                    maxWidth={366}
                >
                    <Button
                        onClick={() =>
                            store.dispatch(Actions.showSignInModal({ isOpen: true, extraParams: { headerValue: HEADER_VALUE.USER_CLICK } }))
                        }
                        variant='primary'
                        children={getText('signInButton')}
                    />
                    <Button
                        onClick={() => store.dispatch(Actions.showRegisterModal({ isOpen: true, openPostBiSignUpModal: true }))}
                        variant='secondary'
                        children={getText('registerButton')}
                    />
                </Grid>
            )}
            {enablePageRenderTracking && !isSkeleton && <PageRenderReport />}
        </Flex>
    );
}

export default wrapFunctionalComponent(PromoListFallback, 'PromoListFallback');
